import { useEffect } from "react";
import { money } from "../lib/format";

const round2 = v => Math.round(v * 100) / 100;

export const METHODS = ["Havale/EFT", "Kredi Kartı", "Nakit", "Çek", "Diğer"];
export const SPLIT_MODES = [
  { key: "income", label: "Gelir oranına göre dağıt" },
  { key: "equal", label: "Eşit böl" },
  { key: "manual", label: "Elle gireceğim" },
];

export const splitAmounts = (total, selected, mode, incomeByAccount) => {
  if (!selected.length || !total) return {};
  if (mode === "equal") {
    const share = round2(total / selected.length);
    const out = {};
    selected.forEach((account, index) => {
      out[account] = index === selected.length - 1 ? round2(total - share * (selected.length - 1)) : share;
    });
    return out;
  }
  const weights = selected.map(account => incomeByAccount?.[account] || 0);
  const sum = weights.reduce((a, b) => a + b, 0);
  if (!sum) return splitAmounts(total, selected, "equal", incomeByAccount);
  const out = {};
  let used = 0;
  selected.forEach((account, index) => {
    if (index === selected.length - 1) out[account] = round2(total - used);
    else {
      const value = round2((total * weights[index]) / sum);
      out[account] = value;
      used = round2(used + value);
    }
  });
  return out;
};

export default function AllocationFields({ form, setField, accounts, payers, payees = [], incomeByAccount, prefix }) {
  const total = Number(form.amount || 0);
  const selected = form.selected || [];
  const allocs = form.allocs || {};
  const distributed = round2(selected.reduce((sum, account) => sum + Number(allocs[account] || 0), 0));
  const diff = round2(total - distributed);

  useEffect(() => {
    if (form.split_mode === "manual" || !selected.length || !total) return;
    const next = splitAmounts(total, selected, form.split_mode, incomeByAccount);
    const changed = selected.some(account => Number(allocs[account] || 0) !== next[account]);
    if (changed) setField("allocs", next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total, form.split_mode, selected.join("|")]);

  const toggle = account => {
    const next = selected.includes(account) ? selected.filter(a => a !== account) : [...selected, account];
    setField("selected", next);
    if (form.split_mode === "manual") {
      const kept = {};
      next.forEach(a => { kept[a] = allocs[a] || ""; });
      setField("allocs", kept);
    }
  };

  return (
    <>
      <div className="field">
        <label>Ödeyen <small className="hint">(parayı gönderen taraf)</small></label>
        <select data-testid={`${prefix}-payer-select`} value={form.payer || ""} onChange={e => setField("payer", e.target.value)}>
          <option value="">Ödeyen seçilmedi</option>
          {payers.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>
      <div className="field">
        <label htmlFor={`${prefix}-payee-select`}>Ödenen <small className="hint">(parayı alan taraf)</small></label>
        <select id={`${prefix}-payee-select`} data-testid={`${prefix}-payee-select`} value={form.payee || ""} onChange={e => setField("payee", e.target.value)}>
          <option value="">Ödenen seçilmedi</option>
          {payees.map(payee => <option key={payee} value={payee}>{payee}</option>)}
        </select>
      </div>
      <div className="field">
        <label>Hangi hesapları kapatıyor? <small className="hint">(birden fazla seçebilirsiniz)</small></label>
        <div className="alloc-chips" data-testid={`${prefix}-account-chips`}>
          {accounts.map(account => (
            <label key={account} className={`perm-item ${selected.includes(account) ? "on" : ""}`}>
              <input type="checkbox" data-testid={`${prefix}-account-${account}`} checked={selected.includes(account)} onChange={() => toggle(account)}/>
              <span>{account}</span>
            </label>
          ))}
        </div>
      </div>
      {selected.length > 1 && (
        <div className="field">
          <label>Dağıtım yöntemi</label>
          <select data-testid={`${prefix}-split-mode`} value={form.split_mode} onChange={e => setField("split_mode", e.target.value)}>
            {SPLIT_MODES.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
        </div>
      )}
      {selected.length > 0 && (
        <div className="alloc-rows" data-testid={`${prefix}-allocations`}>
          {selected.map(account => (
            <div className="alloc-row" key={account}>
              <span>{account}</span>
              <input data-testid={`${prefix}-alloc-${account}`} type="number" min="0" step="0.01"
                disabled={form.split_mode !== "manual" && selected.length > 1}
                value={allocs[account] ?? ""} onChange={e => setField("allocs", { ...allocs, [account]: e.target.value })}/>
              <small>{form.currency}</small>
            </div>
          ))}
          <div className={`alloc-diff ${Math.abs(diff) > 0.05 ? "bad" : "ok"}`} data-testid={`${prefix}-alloc-diff`}>
            {Math.abs(diff) > 0.05
              ? `Dağıtılmayan tutar: ${money(diff, form.currency)}`
              : `Dağıtım tamam · ${money(distributed, form.currency)}`}
          </div>
        </div>
      )}
    </>
  );
}
